/**
 * Shared helpers for `newCopy.ts`'s per-family entries. `whatCausedIt`/
 * `howToFixIt` are authored as real JSX here, not markdown parsed by
 * `LightMdToJsx`, so interpolated links and bold text render as real
 * elements. These helpers exist to avoid re-typing the same link/list
 * markup in every entry.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience } from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import React, { ReactNode } from 'react';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import { TextLinkButtonBox } from '../../../../components/Builders/newux/kit/controls';
import { tokens } from '../../../../components/Builders/newux/kit/tokens';
import { defaultHumanReadableActorName } from '../textHelpers';
import { IssueCopyContext, Issue, TimelineItemRef } from '../types';

export const ctxOf = (group: Issue[]): IssueCopyContext => group[0].ctx;

/** Factory for the one-line `data` cast every non-AUTOMATION family file
 * needs (e.g. `dataOfFamily('CC')`, `dataOfFamily('SENT_ITEM')`).
 * AUTOMATION's raw shape has extra fields (`action`/`instance`) so it keeps
 * its own accessors in `newCopy/automation.tsx`. */
export const dataOfFamily = <F extends Exclude<Issue['raw']['family'], 'AUTOMATION'>>(family: F) => (
  group: Issue[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => (group[0].raw as { family: F; data: any }).data;

/** Real name for `actorKey` on `instance` when one's been resolved (see
 * `resolveGroupActorNames.ts`), else the role-label fallback `legacyCopy.ts`
 * already uses via `humanReadableActorName`. */
export const personLabel = (
  ctx: IssueCopyContext,
  actorKey: string | undefined,
  instance: Doc<IExperience> | undefined
): string => {
  if (!actorKey) return '';
  return (
    ctx.resolveActorFullName?.(actorKey, instance) ||
    (ctx.humanReadableActorName || defaultHumanReadableActorName)(actorKey, ctx.supportingActors)
  );
};

/** The role's label from the template (e.g. "Line Manager"), never the
 * person holding it. Use where the copy says "the X role". */
export const roleLabel = (ctx: IssueCopyContext, actorKey: string | undefined): string => {
  if (!actorKey) return '';
  return (ctx.humanReadableActorName || defaultHumanReadableActorName)(actorKey, ctx.supportingActors);
};

/** Every `t()` call whose text contains a literal `:` must disable i18next's
 * namespace-splitting on that call, or the string silently renders empty.
 * Routing every summary/whatCausedIt/plain-text string through this instead
 * of calling `ctx.t` directly makes that check automatic instead of a thing
 * to remember per call site. */
export const tt = (
  ctx: IssueCopyContext,
  text: string,
  vars?: Record<string, unknown>
): string => ctx.t(text, text.includes(':') ? { ...vars, nsSeparator: false } : vars);

// Only URLs referenced by more than one copy entry live here. A URL used by
// exactly one entry is inlined at that entry instead of added here.
export const HELP_ARTICLES = {
  import: 'https://help.dadohr.com/article/155-trigger-a-manual-import-into-dado',
  editUserProfile:
    'https://help.dadohr.com/article/164-how-to-update-user-data-in-dado#How-to-update-data-directly-in-Dado-_H-np',
  changeStartDateOrManager:
    'https://help.dadohr.com/article/20-change-a-start-date-or-manager-for-one-experience-only',
  slackGrantBotAccess:
    'https://help.dadohr.com/article/25-slack-integration?preview=6113bb2c6ffe270af2a97cce#3-Grant-the-Dado-Bot-access-to-the-relevant-channels-_kFfY',
  ukgIntegration: 'https://help.dadohr.com/article/179-ukg-integration#How-to-re-connect-the-UKG-Pro-integration--ehvx'
} as const;

export const HelpArticle: React.FC<{ href: string }> = ({ href }) => (
  <LinkExternal href={href} style={{ color: tokens.color.blue600 }}>
    📖 <strong>Help article</strong>
  </LinkExternal>
);

export const SupportEmail: React.FC = () => (
  <a href="mailto:help@dadohr.com">help@dadohr.com</a>
);

/** The "wait, then contact support" pair that closes most `API_ERROR`/
 * `DEFAULT`/unknown-issue entries verbatim. */
export const genericRetrySupportSteps = (): ReactNode => (
  <ul>
    <li>
      Wait a few minutes and click <strong>retry</strong>
    </li>
    <li>
      If this still doesn't work, contact <SupportEmail /> who can help diagnose the underlying
      cause of this error. It's helpful if you include a screenshot of this issue and a link to
      this page.
    </li>
  </ul>
);

export const retryStep = (): ReactNode => (
  <li>
    Then come back here and click <strong>retry</strong>
  </li>
);

/** Closing step for CC kinds: no callable re-sends a CC copy, so the modal
 * hides Retry. CC recipients are resolved again on every send, so later
 * copies pick up the fix. */
export const ccNoRetryStep = (): ReactNode => (
  <li>
    Once this is fixed, click <strong>ignore</strong>. This copy can't be resent, but future copies will use
    the updated details.
  </li>
);

/** Closing step for COMPLETE_LMS_MODULE kinds: the enrolment op is aborted
 * for good and nothing re-queues it, so the modal hides Retry. */
export const lmsNoRetryStep = (): ReactNode => (
  <li>
    Once this is fixed, enroll them in the course directly in Docebo, then click <strong>ignore</strong>. Dado
    can't re-run this enrollment.
  </li>
);

/** The import / edit-profile pair that closes most missing-data fixes.
 * Name the field when the surrounding copy has named a single one; leave
 * it out when the copy talks about data in general. */
export const importOrEditProfileSteps = (fieldLabel?: string): ReactNode => (
  <>
    <li>
      Importing from an external system <HelpArticle href={HELP_ARTICLES.import} />
    </li>
    <li>
      Directly editing{' '}
      {fieldLabel ? (
        <>
          the <strong>{fieldLabel}</strong> field
        </>
      ) : (
        'the relevant field(s)'
      )}{' '}
      on their user profile <HelpArticle href={HELP_ARTICLES.editUserProfile} />
    </li>
  </>
);

/** The kit's text link button, inheriting the surrounding font so it reads
 * as part of the sentence it sits in. */
const InlineLinkButton = styled(TextLinkButtonBox)`
  font: inherit;
  white-space: normal;
`;

/** Opens a template timeline item's read-only dialog over the current view.
 * Plain label when the consumer can't open one or the item is unknown. */
export const timelineItemLink = (
  ctx: IssueCopyContext,
  ref: TimelineItemRef | null | undefined,
  label: ReactNode
): ReactNode => {
  if (!ref || !ctx.onOpenTimelineItem) return label;
  const open = ctx.onOpenTimelineItem;
  return (
    <InlineLinkButton type="button" onClick={() => open(ref)}>
      {label}
    </InlineLinkButton>
  );
};

export const automationLink = (
  ctx: IssueCopyContext,
  automationId: string | undefined,
  label: ReactNode = 'automation'
): ReactNode => timelineItemLink(ctx, automationId ? { type: 'automation', id: automationId } : null, label);

export const taskLink = (ctx: IssueCopyContext, taskId: string | undefined, label: ReactNode = 'task'): ReactNode =>
  timelineItemLink(ctx, taskId ? { type: 'task', id: taskId } : null, label);

/** Link to an experience's participants step (where enrollment filters
 * live); plain label without a route or template id. Opens in a new tab,
 * since it leaves this experience's page. */
export const enrollmentFiltersLink = (
  ctx: IssueCopyContext,
  templateId: string | null | undefined,
  label: ReactNode = 'enrollment filters'
): ReactNode => {
  if (!templateId || !ctx.routes) return label;
  return (
    <LinkExternal
      style={{ color: tokens.color.blue600 }}
      href={ctx.routes.experiences.details.edit.url(templateId, { step: 'participants' })}
    >
      {label}
    </LinkExternal>
  );
};

export const userProfileLink = (
  ctx: IssueCopyContext,
  userId: string | null | undefined,
  label: ReactNode = 'user profile'
): ReactNode => {
  if (!userId || !ctx.routes) return label;
  return (
    <LinkExternal style={{ color: tokens.color.blue600 }} href={ctx.routes.users.profile.url(userId)}>
      {label}
    </LinkExternal>
  );
};

export const emailFieldLabel = (emailField: string | undefined): string =>
  emailField === 'personalEmail' ? 'personal email' : 'work email';
